import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import {Translations} from './translations.js';
import template from './ns-line-item.html';
import {RunConfigService, TranslationsUtil, events, ConfigService} from '@nuskin/ns-util';
import {ProductService} from '@nuskin/ns-shop';
import '@polymer/paper-tooltip/paper-tooltip.js';

/**
 * Image Imports
 */
import modalCloseIcon from '../img/ic-close-small.svg';
import warningIcon from '../img/warning-icon.svg';
import tooltipIcon from '../img/icon-info-blue.svg';

/**
 * Component for a line item card on the shopping cart page or the edit adr cart page.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h2>Line Item Component Demo</h2>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-line-item SKU="01003888"></ns-line-item>
 *     <ns-line-item SKU="01110353"></ns-line-item>
 *     <ns-line-item is-adr-cart="true" SKU="01103132"></ns-line-item>
 *   </template>
 * </demo-snippet>
 */
export class NsLineItem extends PolymerElement {
  static get is () {
    return 'ns-line-item';
  }

  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties () {
    return {
      id: {
        type: String
      },
      /**
       * isAdrCart
       * Indicates whether this line item appears on the edit adp cart page or not
       */
      isAdrCart: {
        type: Boolean,
        value: false
      },
      /**
       * language
       * Current language set for component used in translations
       */
      language: {
        type: String
      },
      /**
       * country
       * Current country set for component used for things like regex and markup structure
       */
      country: {
        type: String
      },
      /**
       * translations
       * Current set of translations for label based on language property
       */
      translations: {
        type: JSON
      },
      /**
       * SKU
       * Item number used in product lookup
       */
      SKU: {
        type: String
      },
      /**
       * unitCostInPoints
       * Price of product in line item in points
       */
      unitCostInPoints: {
        type: Number,
        notify: true
      },
      /**
       * unitPrice
       * Price of one product in line item
       */
      unitPrice: {
        type: Number,
        notify: true
      },
      /**
       * unitWholesalePrice
       * Wholesale price of one product in line item
       */
       unitWholeSalePrice: {
        type: Number,
        notify: true
      },
      /**
       * unitPsv
       * Psv earned from one product in line item
       */
      unitPsv: {
        type: Number,
        notify: true,
        observer: "onChangeUnitPsv"
      },
      showPsv: {
        type: Boolean,
        value: false
      },
      /**
       * adrLabel
       * Label for ADR checkbox
       */
      adrLabel: {
        type: String,
      },
      /**
       * oneTimeLabel
       * Label for one time checkbox
       */
      oneTimeLabel: {
        type: String,
      },
      /**
       * purchaseWithPointsLabel
       * Label for purchase with points checkbox
       */
      purchaseWithPointsLabel: {
        type: String,
      },
      /**
       * RequestedQuantity
       * Product quantity selected by the user.
       */
      RequestedQuantity: {
        type: Number,
        observer: 'updateTotals',
        notify: true
      },
      /**
       * totalPriceLabel
       * Label for the total price
       */
      totalPriceLabel: {
        type: String,
      },
      /**
       * isAdr
       * Property for ADR checkbox
       */
      isAdr: {
        type: Boolean,
        value: false,
        notify: true
      },
      /**
       * OneTime
       * Property for one-time checkbox
       */
      OneTime: {
        type: Boolean,
        notify: true,
        value: false,
        observer: 'isOneTimeChange'
      },
      /**
       * PayWithPoints
       * Checkbox for if line item is being purchased with points
       */
      PayWithPoints: {
        type: Boolean,
        notify: true,
        value: false,
        observer: 'purchaseWithPointsChange'
      },
      /**
       * closeIcon
       * Closes the details pane
       */
      closeIcon: {
        type: String,
        value: 'https://www.nuskin.com/apps/shop/components/shop/cart/images/icon_expand_less_24px.svg'
      },
      /**
       * adrIcon
       * Appears when checkbox for isAdr is checked
       */
      adrIcon: {
        type: String,
        value: 'https://www.nuskin.com/content/dam/global/images/logo-adr.svg'
      },
      /**
       * detailPaneHeight
       * Full height of the details pane when it is displayed
       */
      detailPaneHeight: {
        type: String
      },
      /**
       * action
       * Indicates what action status to stamp on this line item for when the cart is next saved
       * @example "UPDATE", "ADD", "DELETE", or ""
       */
      action: {
        type: String,
        notify: true
      },
      /**
       * isInitiated
       * Indicates when initial setup is complete to ensure only user changes cause cart updates
       */
      isInitiated: {
        type: Boolean,
        value: false
      },
      /**
       * Indicates whether the item is elgible for grandfathered pricing or not.
       */
      GrandfatheredPricing: {
        type: Boolean,
        value: false
      },
      /**
       * Encoded value of the close icon used for passing this value to the template
       */
      modalCloseIcon: {
        type: String,
        value: modalCloseIcon
      },
      /**
       * Encoded value of the warning icon used for passing this value to the template
       */
      warningIcon: {
        type: String,
        value: warningIcon
      },
      /**
       * AEM configuration, Show the info tooltip on the adr cart page with One Time Information
       */
      showOneTimeInfoTooltip: {
        type: Boolean
      },
      /**
       * AEM configuration, Show or Hide the PV column in the cart
       */
      showPV: {
        type: Boolean
      },
      /**
       * Span Label Tag, that appears before Product Name in Line Item.
       * Will show 'NEW' when items are added to ADR through Cart.
       */
      lineItemNewStatus: {
        type: Boolean,
        value: false
      },
      /**
       * Whether the redeem points checkbox on the cart is checked or not. It determines whether or not
       * the pay with points chekcbox is hidden on each line item.
       */
      redeemPointsChecked: {
        type: Boolean,
        value: false,
        observer: 'showHidePurchaseWithPoints'
      },
      /**
       * Flag for the last item remaining in an adr to disallow paying with points
       */
      isLastAdrItem: {
        type: Boolean,
        value: false
      },
      /**
       * Flag for the last item remaining in an adr being over the user's points balance
       */
      isOverPointsBalance: {
        type: Boolean,
        computed: '_computeOverPointsBalance(totalsInfo.availablePoints, unitCostInPoints, RequestedQuantity)'
      },
      /**
       * Flag to show the over points balance tooltip
       */
      showOverPointsBalance: {
        type: Boolean,
        computed: '_computeShowOverPointsBalance(redeemPointsChecked, isLastAdrItem, isOverPointsBalance)'
      },
      /**
       * Flag for showing the pay with points checkbox
       */
      showPayWithPoints: {
        type: Boolean,
        computed: '_computeShowPayWithPoints(redeemPointsChecked, isLastAdrItem, isOverPointsBalance, unitCostInPoints)'
      },
      /**
       * Flag to show the one time checkbox
       */
      showOneTimeCheckboxComputed: {
        type: Boolean,
        computed: '_computeShowOneTimeCheckbox(showOneTimeCheckbox, isLastAdrItem)'
      },
      /**
       * Hides the deletion option.
       */
      hideDelete: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-edit'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Encoded value of tooltip icon used for passing this value to the template.
       */
      tooltipIcon: {
        type: String,
        value: tooltipIcon
      },
      /**
       * When true, triggers the screen to scroll to and highlight this line-item.
       */
      justUpdated: {
        type: Boolean,
        notify: true,
        observer: '_determineScrollAndHighlight'
      },
      /**
       * This points balance is the same as the point balance in the cart
       */
      pointsBalance: {
        type: Number,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * total psv earned per line item
       */
      totalLineItemPsv: {
        type: Number,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * total price value per line item
       */
      totalLineItemPrice: {
        type: Number,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * total wholesale price value per line item
       */
       totalLineItemWholesalePrice: {
        type: Number,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * This is the maximum quantity that a product can have
       * Based on the points balance and products points cost of product
       */
      maxNumber: {
        type: Number,
        reflectToAttribute: true
      },
      /**
       * Object that has the cart's totals
       */
      totalsInfo: {
        type: Object,
        notify: true,
        reflectToAttribute: true
      },
      /**
       * Product Name of each line item
       */
      title: {
        type: String,
        value: ''
      },
      /**
       * Image string for each line item
       */
      image: {
        type: String,
        value: ''
      },
      /**
       * Counter binded to ns-adp-cart to notify the number of 'Pay w/Points' items
       */
      purchaseWithPointsCounter: {
        type: Number,
        value: 0,
        notify: true
      },
      /**
       * Sends product info to the cart so it can keep track of deleted line items
       */
      lastItemDeleted: {
        type: Object,
        notify: true
      },
      /**
       * Cart length used for last adr item check
       */
      cartLength: {
        type: Number,
        notify: true
      },
      /**
       * counter to keep track of the last adr item
       */
      oneTimeCounter: {
        type: Number,
        notify: true
      },
      /**
       * hides the line item if it is deleted
       */
      isHidden: {
        type: Boolean,
        value: false,
        notify: true
      },
      /**
       * available quantity
       */
      availableQty: {
        type: Number
      },
      /**
       * variable used to set max-number on ns-number-increment
       */
      maxQuantity: {
        type: Number
      },
      /**
       * variable used to set min-number on ns-number-increment
       */
      minQuantity: {
        type: Number
      },
      /**
       * Collection of toast message objects, shared with the parent (ns-adp-cart).
       * Messages in this collection are displayed by the parent.
       */
      toastMessages: {
        type: Array,
        notify: true
      },
      /**
       * variable used to identify if object is discontinued
       */
      discontinued: {
        type:Boolean,
      },

      /**
       * variable used to inform parent whether item has been removed from subscription.
       */
       removedItems: {
         type: Boolean,
         notify: true
       },

       /**
        * The number of this item's index in the array.
        */
        itemsindex: {
          type: Number,
        }
    };
  }

  static get observers () {
    return [
      '_handlePointsChange(totalsInfo.availablePoints)',
      '_handleOneTimeCounterChange(oneTimeCounter, cartLength, OneTime)',
      '_handleShowDiscontinued(discontinued, showDiscontinuedProduct)'
    ];
  }

  /**
   * ready ()
   * Polymer Lifecycle event
   * Sets translations and adds event listeners
   */
  ready () {
    super.ready();

    if (!this.TRANSLATION_HELPER) this._localize();
    this._setConfigurations();

    let context = this;
    this.addEventListener('dom-change', function () {
      context.showHidePurchaseWithPoints();
    });
    this.ConfigService = ConfigService;
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, Translations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });
  }

  /*
   * Sets configurations to customize functionality for the current market
   */
  _setConfigurations () {
    const DEFAULT = {
      showOneTimeInfoTooltip: false,
      showPV: true,
      showOneTimeCheckbox: false
    };
    const SERVICE = ConfigService.getMarketConfig().adpManage;
    const CONFIG = Object.assign({}, DEFAULT, SERVICE);
    this.showOneTimeInfoTooltip = CONFIG.showOneTimeInfoTooltip;
    this.showPV = CONFIG.showPV;
    this.showOneTimeCheckbox = CONFIG.showOneTimeCheckbox;
    this.showDiscontinuedProduct = CONFIG.showDiscontinuedProduct
  }

  /**
   * Lifecycle method that is called once the component has rendered.
   * Triggers the screen to scroll down to this line-item if it was just added by the user.
   */
  connectedCallback () {
    super.connectedCallback();
    if (this.lineItemNewStatus) {
      this.justUpdated = true;
    }
  }

  /*
   * update total info for cart when RequestedQuantity changes.
   */
  updateTotals (newQty, oldQty) {
    // Handling cases of blank spaces.
    if (newQty === "") {
      newQty = 1;
    }
    if (oldQty === "") {
      oldQty = 1;
    }
    if (newQty && oldQty && (newQty != oldQty)) {
      if (newQty < oldQty) {
        this.set('removedItems', true)
      }
      if (!this.lastItemDeleted) this._clearToastMessages();
      if (this.PayWithPoints && this.unitCostInPoints) {
        const oldItemPointsBalance = (newQty * this.unitCostInPoints * 100) / 100;
        const oldTotalPointsBalance = (this.totalsInfo.availablePoints * 100 - oldItemPointsBalance * 100) / 100;
        const pointsTotal = (oldTotalPointsBalance * 100 + oldQty * (this.unitCostInPoints * 100)) / 100;
        this.set('totalsInfo.availablePoints', pointsTotal);
      } else {
        const oldItemPSVTotal = oldQty * (this.unitPsv * 100) / 100;
        const oldTotalPsv = (this.totalsInfo.totalCartPsv * 100 - oldItemPSVTotal * 100) / 100;
        const newPsv = (newQty * this.unitPsv * 100) / 100;
        const psvTotal = (oldTotalPsv * 100 + newPsv * 100) / 100;
        this.set('totalsInfo.totalCartPsv', psvTotal);
        this.totalLineItemPsv = newPsv;

        const oldItemPriceTotal = oldQty * (this.unitPrice * 100) / 100;
        // remove price of items for this lineItem temporarily.
        const oldTotalPrice = (this.totalsInfo.totalCartPrice * 100 - oldItemPriceTotal * 100) / 100;
        const newPrice = (newQty * this.unitPrice * 100) / 100;
        // add back in the new price to the total
        const priceTotal = (oldTotalPrice * 100 + newPrice * 100) / 100;
        this.set('totalsInfo.totalCartPrice', priceTotal);
        this.totalLineItemPrice = newPrice;

        //need to do basically the same thing for the wholesale price now
        const oldItemWholesaleTotal = oldQty * (this.unitWholeSalePrice * 100) / 100;
        const newWholesalePrice = (newQty * this.unitWholeSalePrice * 100) / 100;
        const oldTotalWholesalePrice = (this.totalsInfo.totalCartWholeSalePrice * 100 - oldItemWholesaleTotal * 100) / 100;
        const wholesalePriceTotal = (oldTotalWholesalePrice * 100 + newWholesalePrice * 100) / 100;
        this.set('totalsInfo.totalCartWholeSalePrice', wholesalePriceTotal);
        this.totalLineItemWholesalePrice = newWholesalePrice;
      }
    }
  }

  /**
   * Observer for this.purchaseWithPoints. Ensures the one time checkbox is checked whenever the purchase
   * with points checkbox is checked.
   */
  purchaseWithPointsChange (newPurchaseWithPoints, oldPurchaseWithPoints) {
    if (oldPurchaseWithPoints !== undefined && oldPurchaseWithPoints !== newPurchaseWithPoints) {
      if (!this.lastItemDeleted) this._clearToastMessages();
      const initialPriceTotal = (this.RequestedQuantity * this.unitPrice * 100) / 100;
      const initialPsvTotal = (this.RequestedQuantity * this.unitPsv * 100) / 100;
      const currentPriceBalance = this.totalsInfo.totalCartPrice;
      const currentPsvBalance = this.totalsInfo.totalCartPsv;

      if (this.PayWithPoints) {
        this.OneTime = true;
        const pointsTotal = ((this.totalsInfo.availablePoints * 100) - (this.RequestedQuantity * this.unitCostInPoints * 100)) / 100;
        this.set('totalsInfo.availablePoints', pointsTotal);
        const priceTotal = ((currentPriceBalance * 100) - (initialPriceTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPrice', priceTotal);
        this.totalLineItemPrice = 0;
        const psvTotal = ((currentPsvBalance * 100) - (initialPsvTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPsv', psvTotal);
        this.totalLineItemPsv = 0;
        this.purchaseWithPointsCounter++;
      } else if (this.redeemPointsChecked && !this.PayWithPoints) {
        const pointsTotal = ((this.totalsInfo.availablePoints * 100) + (this.RequestedQuantity * this.unitCostInPoints * 100)) / 100;
        this.set('totalsInfo.availablePoints', pointsTotal);
        const priceTotal = ((currentPriceBalance * 100) + (initialPriceTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPrice', priceTotal);
        const price = (this.unitPrice * this.RequestedQuantity * 100) / 100;
        this.totalLineItemPrice = price;
        const psvTotal = ((currentPsvBalance * 100) + (initialPsvTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPsv', psvTotal);
        const psv = (this.unitPsv * this.RequestedQuantity * 100) / 100;
        this.totalLineItemPsv = psv;
        this.purchaseWithPointsCounter--;
      } else {
        // This block is entered when the 'redeem points' checkbox is un-checked, !this.redeemPointsChecked
        const priceTotal = ((currentPriceBalance * 100) + (initialPriceTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPrice', priceTotal);
        const price = (this.unitPrice * this.RequestedQuantity * 100) / 100;
        this.totalLineItemPrice = price;

        const psvTotal = ((currentPsvBalance * 100) + (initialPsvTotal * 100)) / 100;
        this.set('totalsInfo.totalCartPsv', psvTotal);
        const psv = (this.unitPsv * this.RequestedQuantity * 100) / 100;
        this.totalLineItemPsv = psv;
        this.purchaseWithPointsCounter = 0;
      }
    }
  }

  /**
   * Observer for this.OneTime. Ensures the purchase with points checkbox is not checked whenever the one time
   * checkbox is not checked.
   */
  isOneTimeChange (newOneTime, oldOneTime) {
    if (!this.lastItemDeleted) this._clearToastMessages();
    if (oldOneTime !== undefined) {
      if (!this.OneTime) {
        this.PayWithPoints = false;
        this.oneTimeCounter--;
      } else {
        this.oneTimeCounter++;
      }
    }
    if (!!newOneTime != !!oldOneTime) {
      const eventData = {
        item: this,
        oneTime: {
          oldOneTime,
          newOneTime
        },
        trigger: 'ns-line-item isOneTimeChange'
      }
      events.publish(events.shop.CART_ITEM_UPDATED, eventData);
    }
  }

  /**
   * Slides the "more details" pane up or down
   */
  toggleShowMoreDetails () {
    this.shadowRoot.querySelector('iron-collapse').toggle();
  }

  /**
   * Saves productInfo about this line item and sends it to the cart, which will then delete this line item
   */
  removeProduct () {
    this.set('isHidden', true);
    this.set('removedItems', true);
    let productInfo = {
      SKU: this.SKU,
      LineID: this.LineID,
      NewItemLineID: this.newItemLineId,
      ParentID: this.ParentID,
      PromoCode: this.PromoCode,
      Custom: this.Custom,
      Pricing: this.Pricing,
      title: this.title,
      unitPrice: this.unitPrice,
      unitPsv: this.unitPsv,
      unitWholeSalePrice: this.unitWholeSalePrice,
      unitCostInPoints: this.unitCostInPoints,
      productDetails: this.productDetails,
      image: this.image,
      imageAlt: this.imageAlt,
      RequestedQuantity: this.RequestedQuantity,
      OneTime: this.OneTime,
      PayWithPoints: this.PayWithPoints,
      GrandfatheredPricing: this.GrandfatheredPricing
    };
    // Update cart totals first
    if (this.PayWithPoints && this.unitCostInPoints) {
      this.set('totalsInfo.availablePoints', ((this.totalsInfo.availablePoints * 100 + (this.RequestedQuantity * this.unitCostInPoints * 100)) / 100));
      this.purchaseWithPointsCounter--;
    } else {
      this.set('totalsInfo.totalCartPsv', ((this.totalsInfo.totalCartPsv * 100 - (this.RequestedQuantity * this.unitPsv * 100)) / 100));
      this.set('totalsInfo.totalCartPrice', ((this.totalsInfo.totalCartPrice * 100 - (this.RequestedQuantity * this.unitPrice * 100)) / 100));
      this.set('totalsInfo.totalCartWholeSalePrice', ((this.totalsInfo.totalCartWholeSalePrice * 100 - (this.RequestedQuantity * this.unitWholeSalePrice * 100)) / 100));
    }

    if (this.OneTime) {
      this.set('oneTimeCounter', this.oneTimeCounter - 1);
    }
    this.set('lastItemDeleted', productInfo);
  }

  /**
   * on-click for the remove button (X). If the delete is allowed (based on this.deleteAllowed), it opens the
   * grandfathered pricing modal if applicable, and otherwise deletes the item. If the delete is not allowed,
   * it dispatches a delete-line-item-not-allowed event.
   */
  removeButtonHandleClick () {
    if (this.hideDelete) return;

    this._clearToastMessages();
    if (!this.isLastAdrItem) {
      if(this.GrandfatheredPricing) {
        this.openGrandfatheredModal();
      } else {
        this.removeProduct();
      }
    } else {
      this.dispatchEvent(new CustomEvent('delete-line-item-not-allowed', {detail: {RequestedQuantity: this.RequestedQuantity, SKU: this.SKU}}));
    }
  }

  /**
   * Shows/hides the purchase with points checkbox on the line item based on the value of
   * redeemPointsChecked, which is passed down from adp-cart.
   */
  showHidePurchaseWithPoints () {
    const wrapper = this.shadowRoot.querySelector('.purchase-with-points-checkbox');
    if (wrapper) {
      if (this.redeemPointsChecked) {
        wrapper.classList.remove('purchase-with-points-not-checked');
      } else {
        wrapper.classList.add('purchase-with-points-not-checked');
        this.PayWithPoints = false;
      }
    }
  }

  /**
   * Opens the grandfathered pricing modal when the user tries to delete an item with grandfathered pricing from the adr.
   */
  openGrandfatheredModal () {
    this.$.grandfatheredModal.open();
  }

  /**
   * on-click for the cancel and close buttons in the grandfathered pricing modal. Closes the modal.
   */
  closeGrandfatheredModal () {
    this.$.grandfatheredModal.close();
  }

  onChangeUnitPsv(psv) {
    this.showPsv = psv !== null;
  }

  _computeShowPayWithPoints (redeemPointsChecked, isLastAdrItem, isOverPointsBalance, unitCostInPoints) {
    return redeemPointsChecked && !isLastAdrItem && !isOverPointsBalance && unitCostInPoints && unitCostInPoints > 0;
  }

  _computeShowOverPointsBalance (redeemPointsChecked, isLastAdrItem, isOverPointsBalance) {
    return redeemPointsChecked && !isLastAdrItem && isOverPointsBalance;
  }

  /**
   * keeps track of each line item's underlying points balance in a points market
   * if there are no points, then the tooltip shouldn't show anyways.
   */
  _computeOverPointsBalance (pointsBalance, points, RequestedQuantity) {
    if (points && !this.PayWithPoints) {
      return ((points * 100 * RequestedQuantity) / 100) > pointsBalance;
    }
    return false;
  }

  /**
   * Observer on this.justUpdated that, if true, triggers the dom to scroll to and highlight this line-item.
   * The cases where this happens are:
   * - User adds new item to the cart
   * - User reinserts an item that had been deleted
   * - User adds an item from the searchbar that is already in the cart, causing that item's quantity to increase
   */
  _determineScrollAndHighlight () {
    if (this.justUpdated){
      if(this.style.display === 'none') {
        // if this line-tem isn't visible yet, define an observer to wait for it.
        let lineObserver = new MutationObserver(() => {
          this._scrollAndHighlight();
          lineObserver.disconnect();
        });
        // Get notified when any this element's parent's children's style changes, to catch when this one is visible
        Array.from(this.parentElement.children).forEach((node) => {
          lineObserver.observe(node, {attributes: true, attributeFilter: ['style']});
        });
      } else {
        // If the line-item is already visible, scroll to it and highlight it
        this._scrollAndHighlight();
      }
    }
  }

  /**
   * Scrolls to this line-item and triggers the css to highlight it.
   * Once the 3-second animation is done, we reset justUpdated.
   */
  _scrollAndHighlight () {
    this.scrollIntoView({behavior: 'smooth'});
    // triggers the highlight animation:
    this.classList.add('just-updated');
    this.justUpdated = false;
    window.setTimeout((context = this) => { context.classList.remove('just-updated');}, 3000);
  }

  /**
   * Show/hide one time checkbox depending on market config and if it is last adr item
   */
  _computeShowOneTimeCheckbox (showOneTimeCheckbox, isLastAdrItem) {
    return showOneTimeCheckbox && !isLastAdrItem;
  }

  /**
   * Observer for each line item to keep track of it's max number based on points
   * @param {Number} [availablePoints] - dynamically updated points balance
   */
  _handlePointsChange() {
    const maxNumber = 99;
    if (this.totalsInfo.availablePoints && this.PayWithPoints) {
      const computedMaxNumber = Math.floor((this.totalsInfo.availablePoints) / (this.unitCostInPoints));
      if (!computedMaxNumber) {
        this.maxNumber = this.RequestedQuantity;
      } else {
        this.maxNumber = (computedMaxNumber > maxNumber) ?
          maxNumber :
          (parseInt(computedMaxNumber) + parseInt(this.RequestedQuantity));
      }
    } else {
      this.maxNumber = maxNumber;
    }
    this.maxQuantity = this.maxNumber;
    if (this.PayWithPoints)
      this.maxQuantity = this.maxNumber < this.maxQuantity ? this.maxNumber : this.maxQuantity;
  }

  /**
  * Observer to handle one time counter changes
  * @param {Number} [oneTimeCounter] - keeps track of number of one time items
  * @param {Number} [cartLength] - length of cart
  * @param {Boolean} [isOneTime] - flag indicates if item is one time
  */
  _handleOneTimeCounterChange() {
    this.isLastAdrItem = (this.OneTime !== undefined && this.oneTimeCounter + 1 === this.cartLength && !this.OneTime);
  }

  /**
   * @return the total price in points for the line item -- unit price times requested quantity.
   */
  _computePointsPriceString(unitCostInPoints, RequestedQuantity) {
    return this._toRoundedString(unitCostInPoints * RequestedQuantity);
  }

  /**
   * It doesn't work to put !unitCostInPoints into a dom-if. Thus, this method.
   * @return !this.unitCostInPoints
   */
  _notUnitCostInPoints () {
    return !this.unitCostInPoints;
  }

  /**
   * Clears all toast messages from the page.
   */
  _clearToastMessages() {
    this.set('toastMessages', []);
  }

  _handleShowDiscontinued(discontinued, showDiscontinuedProduct) {
    this.showDiscontinuedText = discontinued && showDiscontinuedProduct;
  }

  /**
   * 
   * @param {number} n 
   * @param {number} places 
   */
  _toRoundedString(n, places = 0) {
    return n.toFixed(places);
  }
}

customElements.define(NsLineItem.is, NsLineItem);
